import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import './Booking.css';
import { gsap } from 'gsap';
import Loader from '../components/layout/preloader';

const Booking = () => {
  const [bookings, setBookings] = useState([]);
  const [ticketTiers, setTicketTiers] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [userName, setUserName] = useState('');
  const [totalPaid, setTotalPaid] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [quantity, setQuantity] = useState('');
  const [ticketTierId, setTicketTierId] = useState('');
  const [exhibitorId, setExhibitorId] = useState('');
  const [loading, setLoading] = useState(false);

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const tableRowsRef = useRef([]);
  const editModalRef = useRef(null);

  const fetchAll = async () => {
    const { data: bookingData } = await supabase
      .from('booking')
      .select('*')
      .order('created_at', { ascending: false });

    const { data: tiersData } = await supabase.from('ticket_tiers').select('id, tier_name_en');
    const { data: exhibitorsData } = await supabase.from('exhibitors').select('id, name');

    const tiers = tiersData || [];
    const exhs = exhibitorsData || [];

    setTicketTiers(tiers);
    setExhibitors(exhs);

    const merged = (bookingData || []).map((b) => ({
      ...b,
      tier_name: tiers.find((t) => t.id === b.ticket_tier_id)?.tier_name_en || '—',
      exhibitor_name: exhs.find((e) => e.id === b.exhibitor_id)?.name || '—',
    }));

    setBookings(merged);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 4 });

      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: -24 },
        { opacity: 1, y: 0, duration: 0.65 }
      );

      tl.fromTo(
        tableWrapperRef.current,
        { opacity: 0, y: 22 },
        { opacity: 1, y: 0, duration: 0.55 },
        '-=0.3'
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const validRows = tableRowsRef.current.filter(Boolean);
    if (validRows.length === 0) return;
    gsap.fromTo(
      validRows,
      { opacity: 0, x: -18 },
      { opacity: 1, x: 0, duration: 0.38, stagger: 0.05, ease: 'power2.out', delay: 4.2 }
    );
  }, [bookings]);

  const animateModalIn = (ref) => {
    if (!ref.current) return;
    gsap.fromTo(
      ref.current,
      { opacity: 0, scale: 0.94, y: 20 },
      { opacity: 1, scale: 1, y: 0, duration: 0.35, ease: 'power3.out' }
    );
  };

  const animateModalOut = (ref, callback) => {
    if (!ref.current) { callback(); return; }
    gsap.to(ref.current, {
      opacity: 0, scale: 0.94, y: 14,
      duration: 0.22, ease: 'power2.in',
      onComplete: callback,
    });
  };

  useEffect(() => {
    if (showEditModal) setTimeout(() => animateModalIn(editModalRef), 10);
  }, [showEditModal]);

  const updateBooking = async (id) => {
    setLoading(true);
    await supabase.from('booking').update({
      user_name: userName,
      total_paid: parseFloat(totalPaid),
      payment_status: paymentStatus,
      quantity: parseInt(quantity),
      ticket_tier_id: ticketTierId,
      exhibitor_id: exhibitorId,
    }).eq('id', id);
    closeEditModal();
    await fetchAll();
    setLoading(false);
  };

  const deleteBooking = async (id) => {
    await supabase.from('booking').delete().eq('id', id);
    await fetchAll();
  };

  const openEditModal = (booking) => {
    setEditBooking(booking);
    setUserName(booking.user_name || '');
    setTotalPaid(booking.total_paid || '');
    setPaymentStatus(booking.payment_status || '');
    setQuantity(booking.quantity || '');
    setTicketTierId(booking.ticket_tier_id || '');
    setExhibitorId(booking.exhibitor_id || '');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    animateModalOut(editModalRef, () => {
      setShowEditModal(false);
      setEditBooking(null);
      setUserName(''); setTotalPaid(''); setPaymentStatus('');
      setQuantity(''); setTicketTierId(''); setExhibitorId('');
    });
  };

  const getShortId = (id) => {
    if (!id) return '—';
    return 'TDS-' + id.slice(0, 4).toUpperCase();
  };

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard/payments">
        <div className="bk-page" ref={pageRef}>

          <div className="bk-header" ref={headerRef}>
            <div>
              <h1 className="bk-title">BOOKING & PAYMENT LEDGER</h1>
              <p className="bk-subtitle">Track all transactions and payment status</p>
            </div>
          </div>

          <div className="bk-table-wrapper" ref={tableWrapperRef}>
            <table className="bk-table">
              <thead>
                <tr>
                  <th>TRANSACTION ID</th>
                  <th>USER NAME</th>
                  <th>TICKET TYPE</th>
                  <th>EXHIBITION</th>
                  <th>QTY</th>
                  <th>TOTAL PAID</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking, index) => (
                  <tr key={booking.id || index} ref={(el) => (tableRowsRef.current[index] = el)}>
                    <td className="bk-id">{getShortId(booking.id)}</td>
                    <td className="bk-name">{booking.user_name}</td>
                    <td><span className="bk-badge">{booking.tier_name}</span></td>
                    <td>{booking.exhibitor_name}</td>
                    <td>{booking.quantity}</td>
                    <td className="bk-amount">EGP {booking.total_paid?.toLocaleString()}</td>
                    <td>
                      <span className={`bk-status ${
                        booking.payment_status === 'PAID' ? 'bk-status--paid' :
                        booking.payment_status === 'PENDING' ? 'bk-status--pending' :
                        booking.payment_status === 'REFUNDED' ? 'bk-status--refunded' : ''
                      }`}>
                        {booking.payment_status}
                      </span>
                    </td>
                    <td>
                      <div className="bk-actions">
                        <button className="bk-action-btn bk-edit-btn" onClick={() => openEditModal(booking)} title="Edit booking">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button className="bk-action-btn bk-delete-btn" onClick={() => deleteBooking(booking.id)} title="Delete booking">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showEditModal && editBooking && (
            <div className="bk-modal-overlay" onClick={closeEditModal}>
              <div className="bk-modal" ref={editModalRef} onClick={(e) => e.stopPropagation()}>
                <div className="bk-modal-header">
                  <h2 className="bk-modal-title">EDIT BOOKING</h2>
                  <button className="bk-modal-close" onClick={closeEditModal}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div className="bk-modal-body">
                  <div className="bk-form-group">
                    <label>User Name <span className="bk-required">*</span></label>
                    <input type="text" placeholder="Enter user name" value={userName} onChange={(e) => setUserName(e.target.value)} />
                  </div>
                  <div className="bk-form-row">
                    <div className="bk-form-group">
                      <label>Total Paid (EGP) <span className="bk-required">*</span></label>
                      <input type="number" placeholder="0" value={totalPaid} onChange={(e) => setTotalPaid(e.target.value)} />
                    </div>
                    <div className="bk-form-group">
                      <label>Quantity <span className="bk-required">*</span></label>
                      <input type="number" placeholder="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    </div>
                  </div>
                  <div className="bk-form-group">
                    <label>Ticket Type <span className="bk-required">*</span></label>
                    <select value={ticketTierId} onChange={(e) => setTicketTierId(e.target.value)}>
                      <option value="">Select ticket type</option>
                      {ticketTiers.map((t) => (
                        <option key={t.id} value={t.id}>{t.tier_name_en}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bk-form-group">
                    <label>Exhibition <span className="bk-required">*</span></label>
                    <select value={exhibitorId} onChange={(e) => setExhibitorId(e.target.value)}>
                      <option value="">Select exhibition</option>
                      {exhibitors.map((e) => (
                        <option key={e.id} value={e.id}>{e.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bk-form-group">
                    <label>Payment Status <span className="bk-required">*</span></label>
                    <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                      <option value="">Select status</option>
                      <option value="PAID">PAID</option>
                      <option value="PENDING">PENDING</option>
                      <option value="REFUNDED">REFUNDED</option>
                    </select>
                  </div>
                </div>
                <div className="bk-modal-footer">
                  <button className="bk-btn-cancel" onClick={closeEditModal}>Cancel</button>
                  <button className="bk-btn-confirm" onClick={() => updateBooking(editBooking.id)} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </DashboardLayout>
    </>
  );
};

export default Booking;